import React from 'react';
import { Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { GlassCard } from './LiquidComponents';
import { getProjectPath } from '../utils/projectSlug';
import { Project } from '../types';

/** Chunk an array into groups of `size` (last group may be shorter). */
const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/** A single tag pill — matches the site's chip language (About "Core Competencies").
    An <li> (inside the cyclers' <ul>) so adjacent pills keep word boundaries for
    CSS-blind text extractors ("AI Fintech", never "AIFintech"). */
const TagPill: React.FC<{ label: string }> = ({ label }) => (
  <li className="px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap bg-black/[0.04] dark:bg-white/[0.05] [.nardogrey_&]:bg-white/[0.05] border border-black/5 dark:border-white/10 [.nardogrey_&]:border-white/10 text-zinc-600 dark:text-zinc-300 [.nardogrey_&]:text-zinc-300">
    {label}
  </li>
);

/**
 * TagCycler — shows 3 tags at a time inside a FIXED-HEIGHT, clipped container so
 * cycling never changes card height or overflows. With ≤3 tags it renders them
 * statically. With more, it softly crossfades to the next group every ~2.8s,
 * looping. Each instance keeps its own timer, so cards cycle independently.
 */
const TagCycler: React.FC<{ tags: string[] }> = ({ tags }) => {
  const groups = React.useMemo(() => chunk(tags, 3), [tags]);
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (groups.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % groups.length);
    }, 2800);
    return () => clearInterval(id);
  }, [groups.length]);

  // Fixed height (one row of pills) + overflow-hidden → zero layout shift, clipped.
  if (groups.length <= 1) {
    return (
      <div className="relative h-7 overflow-hidden">
        <ul className="flex flex-wrap items-center gap-2 list-none m-0 p-0">
          {(groups[0] || []).map((tag) => <TagPill key={tag} label={tag} />)}
        </ul>
      </div>
    );
  }

  return (
    <div className="relative h-7 overflow-hidden">
      <AnimatePresence initial={false}>
        <motion.ul
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className="absolute inset-0 flex flex-wrap items-center gap-2 list-none m-0 p-0"
        >
          {groups[index].map((tag) => <TagPill key={tag} label={tag} />)}
        </motion.ul>
      </AnimatePresence>
    </div>
  );
};

interface ProjectCardProps {
  project: Project;
  /**
   * Wider layout variant — for the Portfolio 2-col grid, where cards are wider than
   * Home's 3-col cards at lg+. Adds a gentle lg: bump to padding, logo, title and
   * description so the wider card reads intentional (not sparse/stretched). Below lg
   * both variants are identical, so a card looks the same in Home & Portfolio until
   * the grids diverge (Home → 3-col, Portfolio → 2-col).
   */
  wide?: boolean;
  /** Extra classes for the parent grid item (e.g. entrance animation). */
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Shared premium monochrome project card — single source of truth for Home
 * ("Selected Works") and Portfolio. Pure monochrome: no blue/green. Framed logo
 * tile, quiet stage badge, monochrome title (hover → black/white), optional
 * subtitle, reserved 3-line description (keeps dividers aligned across cards),
 * soft-cycling pill tags, role micro-label + Globe→site link. Gentle -translate-y
 * lift on hover, no scale (GlassCard hoverScale={false}).
 */
export const ProjectCard: React.FC<ProjectCardProps> = ({ project, wide = false, className = '', style }) => {
  const navigate = useNavigate();
  const name = project.title || project.name || 'Untitled';

  return (
    <GlassCard
      hoverScale={false}
      style={style}
      className={`h-full flex flex-col group cursor-pointer transition-transform duration-500 ease-out hover:-translate-y-0.5 ${wide ? 'p-6 md:p-8 lg:p-9' : 'p-6 md:p-8'} ${className}`}
      onClick={() => navigate(getProjectPath(project))}
    >
      {/* Top row — framed logo tile + quiet stage badge */}
      <div className={`flex justify-between items-start ${wide ? 'mb-6 lg:mb-7' : 'mb-6'}`}>
        <div
          className={`flex-shrink-0 rounded-2xl flex items-center justify-center p-2.5 bg-black/[0.03] dark:bg-white/[0.04] [.nardogrey_&]:bg-white/[0.04] border border-black/5 dark:border-white/10 [.nardogrey_&]:border-white/10 transition-colors duration-500 group-hover:bg-black/[0.05] dark:group-hover:bg-white/[0.07] [.nardogrey_&]:group-hover:bg-white/[0.07] ${wide ? 'w-14 h-14 md:w-16 md:h-16 lg:w-[72px] lg:h-[72px]' : 'w-14 h-14 md:w-16 md:h-16'}`}
        >
          {project.logoUrl ? (
            <img
              src={project.logoUrl}
              alt={name}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          ) : (
            <span className={`font-bold text-zinc-500 dark:text-zinc-300 ${wide ? 'text-lg lg:text-xl' : 'text-lg'}`}>
              {name.charAt(0)}
            </span>
          )}
        </div>
        {project.stage && (
          <span className="px-2.5 py-1 text-[10px] uppercase tracking-wide font-medium rounded-full bg-black/[0.04] dark:bg-white/[0.05] [.nardogrey_&]:bg-white/[0.05] border border-black/5 dark:border-white/10 [.nardogrey_&]:border-white/10 text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300">
            {project.stage}
          </span>
        )}
      </div>

      {/* Title — monochrome hover (pure black / white), no blue. A real <Link>
          so crawlers see an href to the project page; the whole card stays
          clickable via the GlassCard onClick, and stopPropagation keeps a
          direct title click from navigating twice. Preflight makes the anchor
          inherit color/decoration, so rendering is unchanged. */}
      <h3 className={`font-semibold text-zinc-900 dark:text-white group-hover:text-black dark:group-hover:text-white transition-colors ${wide ? 'text-xl md:text-2xl lg:text-[1.7rem]' : 'text-xl md:text-2xl'}`}>
        <Link to={getProjectPath(project)} onClick={(e) => e.stopPropagation()}>
          {name}
        </Link>
      </h3>

      {/* Subtitle — one-line per-project tagline (omitted gracefully if empty) */}
      {project.subtitle && (
        <p className="mt-1 text-sm font-light text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-400 line-clamp-1">
          {project.subtitle}
        </p>
      )}

      {/* Fixed-height description slot → reserves the same vertical space (3 lines)
          whether the copy is 1 line or 3, so the divider + footer below land at the
          exact same Y on every card. Height lives on this wrapper (not the <p>): an
          explicit height on a line-clamp element kills the clamp/ellipsis, so the <p>
          keeps a pure line-clamp-3 and the wrapper reserves the space + clips.
          The wide variant bumps the copy to ~15px, so its reserved height grows to
          match 3 lines at that size (all Portfolio cards share the variant → aligned). */}
      <div className={`overflow-hidden ${wide ? 'mt-3 mb-6 lg:mb-7 h-[70px] lg:h-[78px]' : 'mt-3 mb-6 h-[70px]'}`}>
        <p className={`text-zinc-600 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300 leading-relaxed line-clamp-3 ${wide ? 'text-sm lg:text-[0.95rem]' : 'text-sm'}`}>
          {project.description}
        </p>
      </div>

      {/* Tags — pills; softly cycle 3-at-a-time when there are more (fixed height, clipped) */}
      <div className={wide ? 'mb-6 lg:mb-7' : 'mb-6'}>
        <TagCycler tags={project.tags} />
      </div>

      {/* Footer — role micro-label + real external site link */}
      <div className={`border-t border-black/5 dark:border-white/5 [.nardogrey_&]:border-white/10 flex justify-between items-center ${wide ? 'pt-6 lg:pt-7' : 'pt-6'}`}>
        <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 dark:text-zinc-500 [.nardogrey_&]:text-zinc-400">
          {project.role || 'Founder'}
        </span>
        {project.websiteUrl && (
          <a
            href={project.websiteUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Visit ${name} website`}
            className="text-zinc-400 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-white [.nardogrey_&]:text-zinc-400 [.nardogrey_&]:hover:text-white transition-colors"
          >
            <Globe size={16} />
          </a>
        )}
      </div>
    </GlassCard>
  );
};
